import DataLoader from 'dataloader'
import { NotFound } from 'fejl'
import { observable, runInAction } from 'mobx'
import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import { type Member } from '@/features/iam/members/Member'
import { type Job } from '@/features/jobs/Job'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { FeatureToggles } from '@/features/feature-toggles'
import { type CsDeskApi } from '@/features/relation-manager/api/relation-manager-api'
import { type FlockApi } from '@/features/relation-manager/api/flock-api'
import {
  RelationManager,
  type RelationManagerProps,
} from '@/features/relation-manager/RelationManager'

/**
 * Store for the relation managers assigned to jobs.
 */
export class RelationManagerStore {
  // Not cached, so that each fetch picks up a reassigned relation manager.
  private readonly _loader = new DataLoader<Job, RelationManager | null, Id>(
    (jobs) => Promise.all(jobs.map((job) => this._fetchCore(job))),
    { cache: false, cacheKeyFn: (job) => job.id },
  )

  /**
   * Job IDs that were fetched and have no relation manager.
   */
  private readonly _unassignedJobIds = observable.set<Id>()

  readonly relationManagers = collection({
    create: () => new RelationManager(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  constructor(
    private readonly _api: CsDeskApi,
    private readonly _flockApi: FlockApi,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
  ) {}

  /**
   * Gets the relation manager assigned to the job. `null` when the job has
   * none, `undefined` when it has not been fetched yet.
   */
  getForJob(jobId: Id): Member | null | undefined {
    const relationManager = this.relationManagers.get(jobId)
    if (relationManager) {
      return relationManager.member
    }

    return this._unassignedJobIds.has(jobId) ? null : undefined
  }

  /**
   * Fetches the relation manager assigned to the job, or `null` when it has
   * none.
   */
  async fetchForJob(job: Job): Promise<Member | null> {
    const relationManager = await this._loader.load(job)
    return relationManager?.member ?? null
  }

  private async _fetchCore(job: Job): Promise<RelationManager | null> {
    const jobId = Id.assert(job.id)
    const userPublicId = await this._fetchUserPublicId(jobId)

    return runInAction(() => {
      if (!userPublicId) {
        this.relationManagers.remove(jobId)
        this._unassignedJobIds.add(jobId)
        return null
      }

      this._unassignedJobIds.delete(jobId)
      return this.relationManagers.set(
        this._mapRelationManagerToProps(job, userPublicId),
      )
    })
  }

  private async _fetchUserPublicId(jobId: string): Promise<Id | null> {
    if (FeatureToggles.flockRelationshipManager()) {
      const resource = await this._flockApi.relationshipManagers
        .get(jobId)
        .catch(NotFound.ignore(null))
      return resource ? Id(resource.user_public_id) : null
    }

    const response = await this._api.relationManagers
      .get(jobId)
      .catch(NotFound.ignore(null))
    return response ? Id(response.data.userPublicId) : null
  }

  private _mapRelationManagerToProps(
    job: Job,
    userPublicId: Id,
  ): RelationManagerProps {
    return {
      id: job.id,
      member: this._primeMemberByPublicId({
        workspaceId: job.workspace.id,
        userPublicId,
      }),
    }
  }
}
